import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDebug } from '@/hooks/useDebug';
import { SystemDebugger } from '@/components/SystemDebugger';
import { DataValidationPanel } from '@/components/debug/DataValidationPanel';
import { BadgeTestButton } from '@/components/debug/BadgeTestButton';
import { ConsoleViewer } from './ConsoleViewer';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/hooks/useAuth';

export function DebugPanel() {
  const { isOpen, setIsOpen, logs, errors, network, clearLogs } = useDebug();
  const { state } = useApp();
  const { user } = useAuth();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader className="sticky top-0 bg-background/80 backdrop-blur z-10">
          <SheetTitle>Developer Debug Panel</SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full overflow-x-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="state">State</TabsTrigger>
              <TabsTrigger value="data">Data</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
              <TabsTrigger value="logs">Logs ({logs.length})</TabsTrigger>
              <TabsTrigger value="network">Network ({network.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-3">
              <Card>
                <CardContent className="pt-4">
                  <SystemDebugger />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="state" className="space-y-3">
              <Card>
                <CardContent className="pt-4 text-xs">
                  <div className="font-medium mb-2">App State</div>
                  <pre className="max-h-64 overflow-auto">{JSON.stringify(state, null, 2)}</pre>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-xs">
                  <div className="font-medium mb-2">Auth Session</div>
                  <pre className="max-h-64 overflow-auto">{JSON.stringify(user, null, 2)}</pre>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="data" className="space-y-3">
              <DataValidationPanel />
            </TabsContent>

            <TabsContent value="actions" className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <BadgeTestButton />
              </div>
            </TabsContent>

            <TabsContent value="logs" className="space-y-3">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={clearLogs}>Clear</Button>
              </div>
              <ConsoleViewer />
            </TabsContent>

            <TabsContent value="network" className="space-y-3">
              <NetworkViewer />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NetworkViewer() {
  const { network } = useDebug();
  return (
    <div className="space-y-2 text-xs">
      {network.slice(0, 200).map(n => (
        <Card key={n.id}>
          <CardContent className="pt-4">
            <div className="font-medium">{n.method} {n.url}</div>
            <div className="opacity-70">{n.status ?? '—'} • {n.ok ? 'OK' : n.error ? 'Error' : '—'} • {n.durationMs ?? '—'}ms</div>
          </CardContent>
        </Card>
      ))}
      {network.length === 0 && <div className="opacity-70">No captured requests yet.</div>}
    </div>
  );
}
